/**
 * CSS da página pública LegisPocket.
 *
 * Fica isolado num escopo próprio (prefixo .lp-) e é injetado apenas quando a
 * página é renderizada, para não vazar estilo para o restante da plataforma.
 * A paleta reproduz o schema da plataforma (src/models/Schema.model.ts na API).
 *
 * Responsividade: a base é fluida (clamp/minmax) e vale para qualquer largura;
 * os media queries no fim do arquivo tratam apenas as mudanças de layout —
 * 1024px (tablet paisagem), 900px (menu compacto), 720px (tablet retrato),
 * 600px (telefone), 400px (telefone pequeno) e viewport baixa em paisagem.
 */

export const legisPocketCss = `
/* Reset mínimo: a página é renderizada antes do <CssBaseline /> do MUI, então a
   margem padrão de 8px do body ainda está valendo. Some junto com o <style> ao
   sair da rota. */
html, body, #root {
    margin: 0;
    padding: 0;
    width: 100%;
    max-width: 100%;
}
body { background: #FFFFFF; overflow-x: hidden; }

.lp-root {
    --lp-roxo-900: #1E0736;
    --lp-roxo-800: #2A0851;
    --lp-roxo-700: #3B0675;
    --lp-roxo-600: #4B1196;
    --lp-roxo-400: #7A38C9;
    --lp-lavanda: #DBCFEA;
    --lp-lavanda-claro: #F1ECF9;
    --lp-cinza-fundo: #F3F5F7;
    --lp-cinza-borda: #E7E3F0;
    --lp-texto: #1E0736;
    --lp-texto-suave: #5C5570;
    --lp-texto-claro: rgba(255, 255, 255, 0.78);
    --lp-verde: #4CD964;
    --lp-amarelo: #FFD200;

    --lp-max: 1160px;
    --lp-gutter: clamp(16px, 4vw, 24px);
    --lp-header-h: 76px;
    --lp-secao-y: clamp(56px, 7.5vw, 108px);
    --lp-card-p: clamp(22px, 2.6vw, 30px);
    --lp-grid-gap: clamp(14px, 1.8vw, 22px);

    /* escala tipográfica fluida — o mínimo garante legibilidade no telefone,
       o máximo evita títulos gigantes em monitores largos */
    --lp-fs-h1: clamp(30px, 5.6vw, 58px);
    --lp-fs-h2: clamp(24px, 3.4vw, 42px);
    --lp-fs-h3: clamp(16.5px, 1.5vw, 19px);
    --lp-fs-lead: clamp(15px, 1.35vw, 18px);
    --lp-fs-corpo: clamp(13.5px, 1.1vw, 15px);
    --lp-fs-mini: clamp(12px, 1vw, 13.5px);

    font-family: 'Montserrat', 'Helvetica Neue', Arial, sans-serif;
    color: var(--lp-texto);
    background: #FFFFFF;
    -webkit-font-smoothing: antialiased;
    -webkit-tap-highlight-color: rgba(122, 56, 201, 0.18);
    text-rendering: optimizeLegibility;
    overflow-x: hidden;
}

.lp-root *,
.lp-root *::before,
.lp-root *::after { box-sizing: border-box; }

/* ATENÇÃO: estes seletores valem (0,1,1) — classe + elemento. Uma regra escrita
   só com a classe do componente (ex.: .lp-faixa-titulo, que vale (0,1,0)) PERDE
   deste reset e tem a margem zerada, mesmo aparecendo depois no arquivo. Quando
   precisar de margem em um <p>, <ul> ou <hN> com classe própria, escreva o
   seletor como .lp-root .minha-classe para empatar em elementos e vencer no
   número de classes. */
.lp-root p { margin: 0; }
.lp-root h1, .lp-root h2, .lp-root h3, .lp-root h4 { margin: 0; font-weight: 700; letter-spacing: -0.02em; line-height: 1.15; }
.lp-root ul { margin: 0; padding: 0; list-style: none; }
.lp-root a { color: inherit; text-decoration: none; }
.lp-root img, .lp-root svg { display: block; max-width: 100%; }
.lp-root button { font: inherit; color: inherit; background: none; border: 0; }

/* nenhum texto longo pode estourar a largura em telas estreitas */
.lp-root h1, .lp-root h2, .lp-root h3, .lp-root h4,
.lp-root p, .lp-root li, .lp-root span, .lp-root blockquote {
    overflow-wrap: break-word;
    word-break: break-word;
}

.lp-container {
    width: 100%;
    max-width: var(--lp-max);
    margin: 0 auto;
    padding: 0 var(--lp-gutter);
}

/* respeita o recorte da tela em celulares com notch, sem quebrar navegadores
   antigos que não entendem max()/env() */
@supports (padding: max(0px)) {
    .lp-container {
        padding-left: max(var(--lp-gutter), env(safe-area-inset-left));
        padding-right: max(var(--lp-gutter), env(safe-area-inset-right));
    }
}

/* ---------------------------------------------------------------- tipografia */
.lp-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: clamp(10px, 0.9vw, 11px);
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--lp-roxo-600);
    background: var(--lp-lavanda-claro);
    border: 1px solid var(--lp-cinza-borda);
    border-radius: 999px;
    padding: 7px 14px;
    line-height: 1.3;
}
.lp-eyebrow--dark {
    color: #E4D9F7;
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.16);
}
.lp-eyebrow-dot {
    flex: 0 0 7px;
    width: 7px; height: 7px; border-radius: 50%;
    background: var(--lp-verde);
    box-shadow: 0 0 0 3px rgba(76, 217, 100, 0.22);
}

.lp-h2 { font-size: var(--lp-fs-h2); }
.lp-h3 { font-size: var(--lp-fs-h3); }
.lp-lead {
    font-size: var(--lp-fs-lead);
    line-height: 1.65;
    font-weight: 400;
    color: var(--lp-texto-suave);
}
.lp-lead--dark { color: var(--lp-texto-claro); }
.lp-grifo {
    background: linear-gradient(120deg, var(--lp-roxo-600), var(--lp-roxo-400));
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
}

.lp-section { padding: var(--lp-secao-y) 0; }
.lp-section--cinza { background: var(--lp-cinza-fundo); }
.lp-section--escuro {
    background:
        radial-gradient(900px 460px at 12% 0%, rgba(122, 56, 201, 0.42), transparent 62%),
        radial-gradient(760px 420px at 92% 100%, rgba(75, 17, 150, 0.55), transparent 60%),
        linear-gradient(160deg, #24073F 0%, var(--lp-roxo-900) 55%, #150425 100%);
    color: #FFFFFF;
}
.lp-section--escuro .lp-h2, .lp-section--escuro .lp-h3 { color: #FFFFFF; }

.lp-section-head { max-width: 720px; margin-bottom: clamp(30px, 4vw, 56px); }
.lp-section-head--centro { margin-left: auto; margin-right: auto; text-align: center; }
.lp-section-head .lp-h2 { margin: clamp(14px, 1.8vw, 18px) 0 clamp(12px, 1.6vw, 16px); }

/* âncoras param abaixo do header fixo, qualquer que seja a altura dele */
.lp-root [id] { scroll-margin-top: calc(var(--lp-header-h) + 12px); }

/* ---------------------------------------------------------------- botões */
.lp-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-height: clamp(46px, 5vw, 52px);
    padding: 0 clamp(20px, 2.4vw, 26px);
    border: 1px solid transparent;
    border-radius: 999px;
    font-family: inherit;
    font-size: clamp(14px, 1.15vw, 15px);
    font-weight: 600;
    line-height: 1.2;
    text-align: center;
    cursor: pointer;
    transition: transform .18s ease, box-shadow .18s ease, background-color .18s ease, color .18s ease, border-color .18s ease;
}
.lp-btn svg { flex: 0 0 18px; width: 18px; height: 18px; }
.lp-btn:hover { transform: translateY(-2px); }
.lp-btn:focus-visible { outline: 3px solid var(--lp-roxo-400); outline-offset: 3px; }

.lp-btn--primario {
    color: #FFFFFF;
    background: linear-gradient(135deg, var(--lp-roxo-600) 0%, var(--lp-roxo-400) 100%);
    box-shadow: 0 10px 26px rgba(75, 17, 150, 0.32);
}
.lp-btn--primario:hover { box-shadow: 0 16px 34px rgba(75, 17, 150, 0.42); }

.lp-btn--claro {
    color: #FFFFFF;
    background: rgba(255, 255, 255, 0.10);
    border-color: rgba(255, 255, 255, 0.30);
    backdrop-filter: blur(6px);
}
.lp-btn--claro:hover { background: rgba(255, 255, 255, 0.18); }

.lp-btn--contorno {
    color: var(--lp-roxo-700);
    background: #FFFFFF;
    border-color: var(--lp-cinza-borda);
}
.lp-btn--contorno:hover { border-color: var(--lp-roxo-400); color: var(--lp-roxo-600); }

.lp-btn--branco {
    color: var(--lp-roxo-700);
    background: #FFFFFF;
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.22);
}

.lp-btn--pequeno {
    min-height: 42px;
    padding: 0 clamp(14px, 1.6vw, 20px);
    font-size: clamp(13px, 1.05vw, 14px);
}

/* ---------------------------------------------------------------- header */
.lp-header {
    position: sticky;
    top: 0;
    z-index: 40;
    background: rgba(30, 7, 54, 0.72);
    backdrop-filter: blur(14px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.10);
    transition: background-color .25s ease, border-color .25s ease;
}
.lp-header--solido {
    background: rgba(21, 4, 37, 0.94);
    border-bottom-color: rgba(255, 255, 255, 0.14);
}
.lp-header-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: clamp(12px, 2vw, 24px);
    min-height: var(--lp-header-h);
}
.lp-header-logo { display: flex; align-items: center; flex: 0 0 auto; }
.lp-header-logo img { width: clamp(104px, 11vw, 132px); height: auto; }

.lp-header-nav { display: flex; align-items: center; gap: clamp(16px, 2.4vw, 30px); }
.lp-header-nav a {
    font-size: clamp(13px, 1.1vw, 14px);
    font-weight: 500;
    white-space: nowrap;
    color: rgba(255, 255, 255, 0.76);
    transition: color .18s ease;
}
.lp-header-nav a:hover { color: #FFFFFF; }
.lp-header-acao { display: flex; align-items: center; gap: 10px; }
.lp-header-acao .lp-btn { color: #D1D5DB; }

/* botão de menu — só aparece nas larguras em que a navegação horizontal sai */
.lp-menu-btn {
    display: none;
    width: 44px;
    height: 44px;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.24);
    background: rgba(255, 255, 255, 0.08);
    cursor: pointer;
}
.lp-menu-btn:focus-visible { outline: 3px solid var(--lp-roxo-400); outline-offset: 2px; }
.lp-menu-btn span {
    display: block;
    position: relative;
    width: 18px; height: 2px;
    border-radius: 2px;
    background: #FFFFFF;
    transition: background-color .2s ease;
}
.lp-menu-btn span::before,
.lp-menu-btn span::after {
    content: '';
    position: absolute;
    left: 0;
    width: 18px; height: 2px;
    border-radius: 2px;
    background: #FFFFFF;
    transition: transform .22s ease, top .22s ease;
}
.lp-menu-btn span::before { top: -6px; }
.lp-menu-btn span::after { top: 6px; }
.lp-menu-btn--aberto span { background: transparent; }
.lp-menu-btn--aberto span::before { top: 0; transform: rotate(45deg); }
.lp-menu-btn--aberto span::after { top: 0; transform: rotate(-45deg); }

.lp-menu {
    display: none;
    border-top: 1px solid rgba(255, 255, 255, 0.12);
    background: rgba(17, 3, 30, 0.98);
    backdrop-filter: blur(14px);
    max-height: calc(100vh - var(--lp-header-h));
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
}
.lp-menu-lista { display: flex; flex-direction: column; padding: 8px 0 16px; }
.lp-menu-lista a {
    display: flex;
    align-items: center;
    min-height: 52px;
    padding: 0 4px;
    font-size: 15px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.86);
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}
.lp-menu-lista a:last-child { border-bottom: 0; }
.lp-menu-lista a:active { color: #FFFFFF; }

/* ---------------------------------------------------------------- hero */
.lp-hero {
    position: relative;
    overflow: hidden;
    padding: clamp(48px, 7vw, 104px) 0 clamp(56px, 8vw, 116px);
    background:
        radial-gradient(1000px 520px at 8% -10%, rgba(122, 56, 201, 0.50), transparent 60%),
        radial-gradient(880px 520px at 100% 10%, rgba(75, 17, 150, 0.60), transparent 58%),
        linear-gradient(165deg, #2B0950 0%, var(--lp-roxo-900) 58%, #130322 100%);
    color: #FFFFFF;
}
.lp-hero::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
        linear-gradient(rgba(255, 255, 255, 0.045) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px);
    background-size: 64px 64px;
    mask-image: radial-gradient(760px 460px at 50% 25%, #000 10%, transparent 78%);
    -webkit-mask-image: radial-gradient(760px 460px at 50% 25%, #000 10%, transparent 78%);
    pointer-events: none;
}
.lp-hero-inner { position: relative; z-index: 1; }
.lp-hero-grid {
    display: grid;
    grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
    gap: clamp(32px, 5vw, 68px);
    align-items: center;
}
.lp-hero-grid > * { min-width: 0; }
.lp-hero h1 {
    margin: clamp(16px, 2.2vw, 22px) 0 clamp(14px, 2vw, 20px);
    font-size: var(--lp-fs-h1);
    line-height: 1.1;
    letter-spacing: -0.03em;
}
.lp-hero-grifo {
    background: linear-gradient(110deg, #C9A6FF 0%, #8F6BFF 45%, #E9DBFF 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
}
.lp-hero-sub { max-width: 560px; }
.lp-hero-ctas { display: flex; flex-wrap: wrap; gap: 12px; margin-top: clamp(24px, 3.4vw, 34px); }
.lp-root .lp-hero-nota {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-top: clamp(18px, 2.2vw, 22px);
    font-size: var(--lp-fs-mini);
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.62);
}
.lp-hero-nota svg { flex: 0 0 16px; width: 16px; height: 16px; margin-top: 1px; }

/* painel "vivo" do hero */
.lp-painel {
    border-radius: 20px;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.05) 100%);
    border: 1px solid rgba(255, 255, 255, 0.18);
    box-shadow: 0 30px 70px rgba(0, 0, 0, 0.40);
    backdrop-filter: blur(10px);
    padding: clamp(16px, 1.8vw, 20px);
}
.lp-painel-topo {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding-bottom: 16px;
    margin-bottom: 16px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.14);
}
.lp-painel-titulo { display: flex; align-items: center; gap: 10px; font-size: clamp(12px, 1.1vw, 13px); font-weight: 600; }
.lp-painel-pulso {
    flex: 0 0 8px;
    width: 8px; height: 8px; border-radius: 50%;
    background: var(--lp-verde);
    box-shadow: 0 0 0 0 rgba(76, 217, 100, 0.55);
    animation: lp-pulso 2.4s infinite;
}
@keyframes lp-pulso {
    0%   { box-shadow: 0 0 0 0 rgba(76, 217, 100, 0.55); }
    70%  { box-shadow: 0 0 0 10px rgba(76, 217, 100, 0); }
    100% { box-shadow: 0 0 0 0 rgba(76, 217, 100, 0); }
}
.lp-painel-tag {
    flex: 0 0 auto;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.10em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.66);
    border: 1px solid rgba(255, 255, 255, 0.20);
    border-radius: 999px;
    padding: 5px 10px;
}
.lp-painel-lista { display: flex; flex-direction: column; gap: 10px; }
.lp-painel-item {
    display: flex;
    gap: 12px;
    padding: clamp(11px, 1.2vw, 13px) clamp(12px, 1.3vw, 14px);
    border-radius: 13px;
    background: rgba(255, 255, 255, 0.07);
    border: 1px solid rgba(255, 255, 255, 0.10);
    animation: lp-entra .5s ease both;
}
.lp-painel-item:nth-child(1) { animation-delay: .10s; }
.lp-painel-item:nth-child(2) { animation-delay: .25s; }
.lp-painel-item:nth-child(3) { animation-delay: .40s; }
.lp-painel-item:nth-child(4) { animation-delay: .55s; }
@keyframes lp-entra {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: none; }
}
.lp-painel-icone {
    flex: 0 0 34px;
    width: 34px; height: 34px;
    display: grid; place-items: center;
    border-radius: 10px;
    background: rgba(201, 166, 255, 0.18);
    color: #D9C4FF;
}
.lp-painel-icone svg { width: 17px; height: 17px; }
.lp-painel-corpo { min-width: 0; }
.lp-painel-corpo strong { display: block; font-size: clamp(12.5px, 1.1vw, 13px); font-weight: 600; margin-bottom: 3px; }
.lp-painel-corpo span { display: block; font-size: clamp(11.5px, 1vw, 12px); line-height: 1.5; color: rgba(255, 255, 255, 0.66); }
.lp-painel-rodape {
    display: flex; align-items: flex-start; gap: 8px;
    margin-top: 16px; padding-top: 14px;
    border-top: 1px solid rgba(255, 255, 255, 0.14);
    font-size: clamp(11.5px, 1vw, 12px); line-height: 1.5; color: rgba(255, 255, 255, 0.62);
}
.lp-painel-rodape svg { flex: 0 0 15px; width: 15px; height: 15px; color: #C9A6FF; }

/* ---------------------------------------------------------------- números */
.lp-numeros {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 1px;
    margin-top: clamp(36px, 5vw, 68px);
    background: rgba(255, 255, 255, 0.14);
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 16px;
    overflow: hidden;
}
.lp-numero {
    background: rgba(20, 4, 36, 0.34);
    padding: clamp(18px, 2.2vw, 24px) clamp(16px, 2vw, 22px);
}
.lp-numero strong {
    display: block;
    font-size: clamp(17px, 1.9vw, 26px);
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.25;
    margin-bottom: 6px;
}
.lp-numero span { font-size: var(--lp-fs-mini); line-height: 1.5; color: rgba(255, 255, 255, 0.66); }

/* ---------------------------------------------------------------- fontes/cobertura */
.lp-faixa { padding: clamp(26px, 3.4vw, 34px) 0; background: #FFFFFF; border-bottom: 1px solid var(--lp-cinza-borda); }
.lp-root .lp-faixa-titulo {
    text-align: center;
    font-size: clamp(10px, 0.9vw, 11px);
    font-weight: 600;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    line-height: 1.5;
    color: #8E88A0;
    margin-bottom: 20px;
}
.lp-chips { display: flex; flex-wrap: wrap; justify-content: center; gap: clamp(8px, 1vw, 10px); }
.lp-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: clamp(12px, 1.1vw, 13px);
    font-weight: 500;
    line-height: 1.3;
    color: var(--lp-roxo-700);
    background: var(--lp-lavanda-claro);
    border: 1px solid var(--lp-cinza-borda);
    border-radius: 999px;
    padding: clamp(7px, 0.9vw, 9px) clamp(12px, 1.5vw, 16px);
}
.lp-chip svg { flex: 0 0 15px; width: 15px; height: 15px; color: var(--lp-roxo-400); }

/* ---------------------------------------------------------------- grids/cards */
.lp-grid { display: grid; gap: var(--lp-grid-gap); }
.lp-grid > * { min-width: 0; }
.lp-grid--2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.lp-grid--3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.lp-grid--4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.lp-grid--espacado { margin-top: var(--lp-grid-gap); }

.lp-card {
    position: relative;
    height: 100%;
    padding: var(--lp-card-p);
    background: #FFFFFF;
    border: 1px solid var(--lp-cinza-borda);
    border-radius: 18px;
    transition: transform .22s ease, box-shadow .22s ease, border-color .22s ease;
}
.lp-card:hover {
    transform: translateY(-4px);
    border-color: rgba(122, 56, 201, 0.36);
    box-shadow: 0 18px 40px rgba(30, 7, 54, 0.10);
}
.lp-card h3 { font-size: var(--lp-fs-h3); margin-bottom: 10px; }
.lp-card p { font-size: var(--lp-fs-corpo); line-height: 1.65; color: var(--lp-texto-suave); }

.lp-card-icone {
    width: clamp(42px, 4.4vw, 46px);
    height: clamp(42px, 4.4vw, 46px);
    display: grid; place-items: center;
    margin-bottom: clamp(14px, 1.8vw, 20px);
    border-radius: 13px;
    color: #FFFFFF;
    background: linear-gradient(135deg, var(--lp-roxo-700) 0%, var(--lp-roxo-400) 100%);
    box-shadow: 0 8px 20px rgba(75, 17, 150, 0.26);
}
.lp-card-icone svg { width: clamp(20px, 2vw, 22px); height: clamp(20px, 2vw, 22px); }

.lp-card--passo h3 { margin-top: 6px; padding-right: clamp(40px, 5vw, 52px); }
.lp-card-passo {
    position: absolute;
    top: clamp(16px, 2vw, 22px);
    right: var(--lp-card-p);
    font-size: clamp(30px, 3.6vw, 40px);
    font-weight: 700;
    line-height: 1;
    color: rgba(122, 56, 201, 0.13);
}

/* cards dos agentes (fundo escuro) */
.lp-agente {
    height: 100%;
    padding: var(--lp-card-p);
    border-radius: 18px;
    background: rgba(255, 255, 255, 0.055);
    border: 1px solid rgba(255, 255, 255, 0.13);
    transition: transform .22s ease, background-color .22s ease, border-color .22s ease;
}
.lp-agente:hover {
    transform: translateY(-4px);
    background: rgba(255, 255, 255, 0.09);
    border-color: rgba(201, 166, 255, 0.45);
}
.lp-agente-topo { display: flex; align-items: center; gap: 13px; margin-bottom: clamp(12px, 1.6vw, 16px); }
.lp-agente-icone {
    flex: 0 0 auto;
    width: clamp(38px, 4vw, 42px);
    height: clamp(38px, 4vw, 42px);
    display: grid; place-items: center;
    border-radius: 12px;
    background: linear-gradient(135deg, rgba(201, 166, 255, 0.30), rgba(122, 56, 201, 0.42));
    border: 1px solid rgba(255, 255, 255, 0.18);
    color: #E4D9F7;
}
.lp-agente-icone svg { width: clamp(18px, 1.9vw, 20px); height: clamp(18px, 1.9vw, 20px); }
.lp-agente h3 { font-size: var(--lp-fs-h3); }
.lp-agente p { font-size: var(--lp-fs-corpo); line-height: 1.65; color: var(--lp-texto-claro); }
.lp-agente-tag {
    display: inline-block;
    margin-top: clamp(12px, 1.6vw, 16px);
    font-size: clamp(10px, 0.9vw, 11px);
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #D9C4FF;
    background: rgba(201, 166, 255, 0.14);
    border-radius: 999px;
    padding: 5px 12px;
}

.lp-governanca {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 14px clamp(20px, 2.6vw, 32px);
    margin-top: clamp(24px, 3.4vw, 34px);
    padding: clamp(18px, 2.4vw, 22px) clamp(18px, 2.6vw, 26px);
    border-radius: 16px;
    background: rgba(0, 0, 0, 0.22);
    border: 1px solid rgba(255, 255, 255, 0.13);
}
.lp-governanca-item { display: flex; align-items: flex-start; gap: 10px; font-size: var(--lp-fs-corpo); line-height: 1.5; color: var(--lp-texto-claro); }
.lp-governanca-item svg { width: 17px; height: 17px; color: var(--lp-verde); flex: 0 0 17px; margin-top: 1px; }

/* ---------------------------------------------------------------- notificações */
.lp-split {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: clamp(32px, 5vw, 64px);
    align-items: center;
}
.lp-split > * { min-width: 0; }
.lp-split .lp-h2 { margin: clamp(14px, 1.8vw, 18px) 0 clamp(12px, 1.6vw, 16px); }
.lp-root .lp-lista-check { display: flex; flex-direction: column; gap: clamp(12px, 1.6vw, 16px); margin-top: clamp(20px, 2.8vw, 28px); }
.lp-lista-check li { display: flex; gap: 13px; font-size: var(--lp-fs-lead); line-height: 1.6; color: var(--lp-texto-suave); }
.lp-lista-check strong { color: var(--lp-texto); font-weight: 600; }
.lp-lista-check svg { flex: 0 0 20px; width: 20px; height: 20px; margin-top: 2px; color: var(--lp-roxo-400); }

.lp-zap {
    width: 100%;
    max-width: 400px;
    margin: 0 auto;
    padding: clamp(16px, 2vw, 22px);
    border-radius: 22px;
    background: #FFFFFF;
    border: 1px solid var(--lp-cinza-borda);
    box-shadow: 0 24px 60px rgba(30, 7, 54, 0.12);
}
.lp-zap-topo {
    display: flex; align-items: center; gap: 12px;
    padding-bottom: 16px; margin-bottom: 16px;
    border-bottom: 1px solid var(--lp-cinza-borda);
}
.lp-zap-avatar {
    flex: 0 0 40px;
    width: 40px; height: 40px;
    display: grid; place-items: center;
    border-radius: 50%;
    color: #FFFFFF;
    background: linear-gradient(135deg, var(--lp-roxo-700), var(--lp-roxo-400));
    font-size: 13px; font-weight: 700;
}
.lp-zap-topo strong { display: block; font-size: 14px; }
.lp-zap-topo span { display: block; font-size: 11.5px; color: var(--lp-verde); font-weight: 600; }
.lp-zap-msgs { display: flex; flex-direction: column; gap: 12px; }
.lp-zap-msg {
    position: relative;
    padding: 13px 15px;
    border-radius: 14px 14px 14px 4px;
    background: var(--lp-lavanda-claro);
    border: 1px solid var(--lp-cinza-borda);
    font-size: clamp(12.5px, 1.15vw, 13.5px);
    line-height: 1.6;
    color: var(--lp-texto);
}
.lp-zap-msg b { color: var(--lp-roxo-600); }
.lp-zap-hora {
    display: block;
    margin-top: 7px;
    font-size: 10.5px;
    color: #9A93AB;
    text-align: right;
}
.lp-zap-selo {
    display: inline-flex; align-items: center; gap: 6px;
    margin-bottom: 8px;
    font-size: 10px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
    color: var(--lp-roxo-600);
}
.lp-zap-selo::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: var(--lp-amarelo); }

/* ---------------------------------------------------------------- dashboard */
.lp-dash {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: var(--lp-grid-gap);
}
.lp-dash > * { min-width: 0; }
.lp-dash-item {
    padding: clamp(18px, 2.2vw, 22px);
    border-radius: 16px;
    background: #FFFFFF;
    border: 1px solid var(--lp-cinza-borda);
}
.lp-dash-item h4 { font-size: clamp(13.5px, 1.2vw, 14px); margin-bottom: 8px; display: flex; align-items: center; gap: 9px; }
.lp-dash-item h4 svg { flex: 0 0 17px; width: 17px; height: 17px; color: var(--lp-roxo-400); }
.lp-dash-item p { font-size: var(--lp-fs-corpo); line-height: 1.6; color: var(--lp-texto-suave); }

/* ---------------------------------------------------------------- citação */
.lp-citacao {
    max-width: 860px;
    margin: 0 auto;
    text-align: center;
}
.lp-citacao blockquote {
    margin: clamp(16px, 2.4vw, 22px) 0 clamp(16px, 2.4vw, 22px);
    font-size: clamp(18px, 2.6vw, 30px);
    font-weight: 600;
    line-height: 1.4;
    letter-spacing: -0.02em;
}
.lp-citacao > span { display: block; font-size: var(--lp-fs-mini); line-height: 1.7; color: var(--lp-texto-claro); }

/* ---------------------------------------------------------------- cta final */
.lp-cta {
    position: relative;
    overflow: hidden;
    text-align: center;
    padding: clamp(38px, 6vw, 76px) clamp(22px, 5vw, 64px);
    border-radius: clamp(18px, 2.4vw, 26px);
    color: #FFFFFF;
    background:
        radial-gradient(620px 340px at 20% 0%, rgba(122, 56, 201, 0.55), transparent 62%),
        linear-gradient(135deg, var(--lp-roxo-700) 0%, var(--lp-roxo-900) 100%);
    box-shadow: 0 30px 70px rgba(30, 7, 54, 0.28);
}
.lp-cta h2 { font-size: clamp(23px, 3.4vw, 40px); margin-bottom: clamp(14px, 1.8vw, 18px); }
.lp-cta p { max-width: 620px; margin: 0 auto; color: var(--lp-texto-claro); font-size: var(--lp-fs-lead); line-height: 1.65; }
.lp-cta-nota { margin-top: clamp(16px, 2.2vw, 22px) !important; font-size: var(--lp-fs-mini) !important; color: rgba(255, 255, 255, 0.58) !important; }

/* ---------------------------------------------------------------- footer */
.lp-footer { background: var(--lp-roxo-900); color: rgba(255, 255, 255, 0.62); padding: clamp(32px, 4vw, 44px) 0 clamp(26px, 3.4vw, 34px); }
.lp-footer-inner {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: clamp(16px, 2.4vw, 20px);
}
.lp-footer img { width: clamp(104px, 11vw, 124px); height: auto; }
.lp-footer-links { display: flex; flex-wrap: wrap; gap: clamp(14px, 2vw, 22px); font-size: clamp(12.5px, 1.1vw, 13px); }
.lp-footer-links a { padding: 4px 0; transition: color .18s ease; }
.lp-footer-links a:hover { color: #FFFFFF; }
.lp-footer-copy {
    width: 100%;
    padding-top: clamp(18px, 2.4vw, 24px);
    margin-top: 4px;
    border-top: 1px solid rgba(255, 255, 255, 0.12);
    font-size: var(--lp-fs-mini);
    line-height: 1.7;
}

/* ---------------------------------------------------------------- reveal */
.lp-reveal { opacity: 0; transform: translateY(22px); transition: opacity .6s ease, transform .6s ease; }
.lp-reveal.lp-visivel { opacity: 1; transform: none; }

/* ================================================================ responsivo */

/* monitores largos: mais respiro sem esticar a linha de leitura */
@media (min-width: 1600px) {
    .lp-root { --lp-max: 1280px; }
}

/* tablet paisagem */
@media (max-width: 1024px) {
    .lp-hero-grid { grid-template-columns: minmax(0, 1fr); }
    .lp-hero-sub { max-width: 640px; }
    .lp-painel { max-width: 560px; margin: 0 auto; }
    .lp-grid--4 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .lp-grid--3 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .lp-dash { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .lp-numeros { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

/* a navegação horizontal deixa de caber: entra o menu compacto */
@media (max-width: 900px) {
    .lp-root { --lp-header-h: 68px; }
    .lp-header-nav { display: none; }
    .lp-header-acao { display: none; }
    .lp-menu-btn { display: flex; }
    .lp-menu { display: block; }
}

/* tablet retrato */
@media (max-width: 720px) {
    .lp-grid--2, .lp-grid--3, .lp-grid--4 { grid-template-columns: minmax(0, 1fr); }
    .lp-split { grid-template-columns: minmax(0, 1fr); }
    .lp-dash { grid-template-columns: minmax(0, 1fr); }
    .lp-hero-nota { max-width: 460px; }
    .lp-section-head { margin-bottom: clamp(26px, 5vw, 36px); }
}

/* telefone */
@media (max-width: 600px) {
    .lp-root { --lp-header-h: 62px; }
    .lp-hero-ctas { gap: 10px; }
    .lp-hero-ctas .lp-btn { width: 100%; }
    .lp-numeros { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .lp-numero { padding: 16px 14px; }
    .lp-numero strong { font-size: 15px; }
    .lp-numero span { font-size: 11.5px; }
    .lp-chips { gap: 8px; }
    .lp-governanca { grid-template-columns: minmax(0, 1fr); }
    .lp-zap { max-width: none; }
    .lp-citacao blockquote { text-align: left; }
    .lp-footer-inner { flex-direction: column; align-items: flex-start; }
    .lp-footer-links { gap: 12px 18px; }
    /* alvo de toque confortável nos links do rodapé */
    .lp-footer-links a { min-height: 32px; display: flex; align-items: center; }
}

/* telefone pequeno (iPhone SE, Galaxy A0x) */
@media (max-width: 400px) {
    .lp-root { --lp-fs-h1: 27px; }
    .lp-numeros { grid-template-columns: minmax(0, 1fr); }
    .lp-eyebrow { letter-spacing: 0.10em; padding: 6px 12px; }
    .lp-painel-topo { flex-wrap: wrap; }
    .lp-agente-topo { align-items: flex-start; }
    .lp-lista-check li { gap: 10px; }
    .lp-lista-check svg { flex-basis: 18px; width: 18px; height: 18px; }
}

/* telefone deitado / janela baixa: encolhe o topo para sobrar conteúdo */
@media (max-height: 520px) and (orientation: landscape) {
    .lp-root { --lp-header-h: 56px; --lp-secao-y: 44px; }
    .lp-hero { padding: 40px 0 48px; }
    .lp-menu { max-height: calc(100vh - 56px); }
}

/* dispositivos sem hover (touch): sem transform no toque, que "prende" o card */
@media (hover: none) {
    .lp-card:hover, .lp-agente:hover, .lp-btn:hover { transform: none; }
}

@media (prefers-reduced-motion: reduce) {
    .lp-root *, .lp-root *::before, .lp-root *::after {
        animation-duration: .001ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: .001ms !important;
    }
    .lp-reveal { opacity: 1; transform: none; }
}
`;
